import classNames from 'classnames';
import { KeyboardEvent } from 'react';
import { CurrencyInput } from '../../../../../components/CurrencyInput';
import { InputAddon } from '../../../../../components/InputAddon';
import { InputGroup } from '../../../../../components/InputGroup';
import { ClusterCellSkin, NetworkPriceStatus } from '../../types';
import { NetworkPriceStatusTag } from '../NetworkPriceStatusTag';
import { NetworkVariationIndicator } from '../NetworkVariationIndicator';
import styles from './NetworkPriceInputCell.module.scss';

type NetworkPriceInputCellProps = {
  ariaLabel: string;
  // Inputs sharing a column move the focus down among themselves on Enter.
  column: string;
  value: number | null;
  skin: ClusterCellSkin;
  variation: number | null;
  priceStatus: NetworkPriceStatus | null;
  isPending: boolean;
  className?: string;
  onChange: (value: number | null) => void;
  onCommit: () => void;
};

// Enter moves down the same column, like the price manager; scoped to this table.
const focusNextColumnInput = (input: HTMLInputElement, column: string): void => {
  const scope = input.closest('.rs-table') ?? document;
  const inputs = Array.from(scope.querySelectorAll<HTMLInputElement>('input[data-price-column]')).filter(item => item.dataset.priceColumn === column);
  const nextInput = inputs[inputs.indexOf(input) + 1];
  input.blur();
  nextInput?.focus();
};

export const NetworkPriceInputCell = ({
  ariaLabel,
  column,
  value,
  skin,
  variation,
  priceStatus,
  isPending,
  className,
  onChange,
  onCommit,
}: NetworkPriceInputCellProps) => {
  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    focusNextColumnInput(event.currentTarget, column);
  };

  return (
    <div className={classNames(styles.container, className)}>
      <InputGroup className={styles.inputGroup}>
        <InputAddon>R$</InputAddon>
        <CurrencyInput
          aria-label={ariaLabel}
          data-price-column={column}
          value={value}
          allowNull
          placeholder="--"
          allowNegative={false}
          decimalSeparator=","
          thousandSeparator="."
          precision={2}
          selectOnFocus
          skin={skin}
          className={styles.input}
          disabled={isPending}
          onChangeEvent={(_event, _mask, changedValue) => onChange(changedValue)}
          onBlur={onCommit}
          onKeyDown={handleKeyDown}
        />
      </InputGroup>
      <div className={styles.indicators}>
        <NetworkVariationIndicator value={variation} suffix="%" />
        <NetworkPriceStatusTag priceStatus={priceStatus} />
      </div>
    </div>
  );
};
